import store from '@/store';
import Vue from 'vue';

// 注意,如果外部导入了store并同时导入了StoreShell会导致报错,后者只能后于前者初始化

const moduleName = '__lab';

store.registerModule(moduleName, {
	namespaced: true,
	state: {},
})

const __lab = store.state.__lab;
let id = 0;

/**
 * 说明一下这个类型
 * 首先是类型声明:要求T为一个object,K则是一个由T的键值组成的数组
 * 其次是功能设计,此类型设计为从K数组描述的键值组中获取键值,
 * 并将此键值组全数映射为T的子集,可参见下面的示例
 * 最后分析一下运行原理:K的类型是一个数组,故K[number]就是数组内元素的集合,
 * 取得了元素集合,就可以用in动态定义该集合,T[Z]则是通过T接口的类型声明定义其子集的类型
 */
type ObjectFromKeyArray<Master extends object, KeyArray extends Array<keyof Master>> = {
	[Z in KeyArray[number]]: Master[Z];
}

/**
 * 对于Store的拆分类,将自身的state直接暴露,组件注入则直接使用getters
 * 剩下的mutation和action整合为类方法对外开放
 */
export abstract class commonStoreShell<T extends object, K extends object = Record<keyof object, () => unknown>> {
	/**虽然对外暴露自身数据,但请谨慎修改 */
	public readonly state: T;
	/**getters是组件的数据来源,不建议直接使用state */
	public abstract getters: K;

	constructor() {
		Vue.set(__lab, id, this.getStateInit());
		this.state = __lab[id];
		id++;
	}

	protected abstract getStateInit(): T;

	/**getters映射方法 */
	public mapGetters<S extends (keyof K)[]>(getters: S): ObjectFromKeyArray<K, S> {
		return getters.reduce((prev: any, current) => {
			prev[current] = (this.getters as any)[current];
			return prev;
		}, {}) as any;
	}

	/**state映射方法,仅为了快捷访问,修改时请注意类型安全 */
	public mapStates<S extends (keyof T)[]>(states: S): ObjectFromKeyArray<T, S> {
		return states.reduce((prev: any, current) => {
			prev[current] = {
				get: () => this.state[current],
				set: (v) => {
					this.state[current] = v;
				}
			}
			return prev;
		}, {}) as any;
	}

	/**如要对数据进行修改  
	 * 一是通过state直接修改,随后修改会反映到getters上
	 * 二是通过public方法进行修改,这同样会间接修改state,但是更加安全
	 */
}

//#region plugin代码

export interface Newable<T> {
	new() : T
}

export function usePlugin<T extends Newable<unknown>>(classOrg: T, plugin: Function | Function[]) {
	let plg;

	if (typeof plugin === 'function') {
		plg = [plugin];
	}
	else {
		plg = plugin;
	}

	for (let item of plg) {
		Object.getOwnPropertyNames(item.prototype).forEach(name => {
			if (!(name in classOrg.prototype)) {
				classOrg.prototype[name] = item.prototype[name];
			}
		})
	}

	return classOrg;
}

// 伪造一个this环境以通过编译,实际使用时需要保证逻辑确实存在,即伪造的this是真实this的子集
type ddp_This<dirtyKeys extends string> = {
	state: Record<dirtyKeys, boolean>
}
export class DirtyDetectionPlugin<dirtyKeys extends string> {
	// this需满足条件: state 内指定的 dirtyKeys 必须为boolean
	// 例如: 若对dirtyKeys 传入 'a' | 'b',在父store内必有 state.a, state.b 类型为 boolean

	/**
	 * 创建一个自动更新委托,由store来负责管理脏数据的刷新
	 * @param vm vm实例
	 * @param key 要委托检查的key
	 * @param refresh 刷新函数
	 */
	public autoUpdateDelegate(this: ddp_This<dirtyKeys>, vm: Vue, key: dirtyKeys, refresh: () => void) {
		delegate(vm, () => this.state[key], (newVal) => {
			if (newVal) {
				refresh();
			}
		})
	}

	/**
	 * 将数据标为脏数据并委托刷新
	 * @param key 要置脏的key
	 */
	public setDirty(this: ddp_This<dirtyKeys>, key: dirtyKeys) {
		this.state[key] = true;
	}
}

//#endregion

export function delegate(vm: Vue, getter: () => unknown, watcher: (newVal: any) => void, immediate = false) {
	vm.$watch(getter, watcher, { immediate });
}
